/**
 * Checks the user's activity on the page and keeps the session alive by calling the keepAlive endpoint.
 * If the user remains inactive for a large period of time, notify the user that they will be logged out
 * automatically if they remain inactive and then, if they remain idle, log them out.
 */
import React, {
  useState,
  useEffect,
  useRef,
  useContext
} from 'react';
import { useIdleTimer } from 'react-idle-timer'
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import CSRFContext from './csrf';

const TIMEOUT = 15 * 60 * 1000; // 15 minutes
const PROMPT_TIMEOUT = 2 * 60 * 1000; // 2 minutes before timeout
const KEEPALIVE_INTERVAL = 5 * 1000; // Every 5 seconds
const KEEPALIVE_EVENT_LISTENERS = ['keyup', 'mouseup', 'mousemove', 'touchend', 'touchmove'];

/**
 * Displays a countdown timer in the idle modal
 * @param {() => integer} retrieves the amount of time (in ms) until the idle timer times out
 */
const CountDown = ({ getRemainingTime }) => {
  const [remaining, setRemaining] = useState(Math.ceil(getRemainingTime() / 1000));

  // Increments the time remaining counter
  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000))
    }, 500)

    return () => {
      clearInterval(interval)
    }
  }, []);

  // Calculates the time remaining percentage (counting down to zero percent)
  const remainingPercentage = Math.ceil((remaining / (PROMPT_TIMEOUT / 1000)) * 100);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <CircularProgress
        variant='determinate'
        value={remainingPercentage}
        sx={{
          '& .MuiCircularProgress-circleDeterminate': {
            // eslint-disable-next-line no-nested-ternary
            stroke: remaining <= 10 ? '#d32f2f' : (remaining <= 30 ? '#ed6c02' : '#2e7d32')
          }
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          variant='caption'
          component='div'
          sx={{ color: 'text.secondary', fontWeight: 500 }}
        >
          {remaining}s
        </Typography>
      </Box>
    </Box>
  )
};

CountDown.propTypes = {
  getRemainingTime: PropTypes.func.isRequired
};

/**
 * Wrapper that checks the user's idle time, notifies them if they've been idle for too long, and
 * logs them out if they remain idle.
 */
const IdleChecker = ({ children }) => {
  const [open, setOpen] = useState(false);
  const keepAlivePing = useRef(false);
  const csrfToken = useContext(CSRFContext);

  // Handles the keep alive call
  function keepAlive () {
    if (!keepAlivePing.current) {
      keepAlivePing.current = true;

      // Sleeps the keep alive check for a period time before allowing it to be called again
      setTimeout(() => {
        keepAlivePing.current = false;
      }, KEEPALIVE_INTERVAL);

      fetch('/keepAlive/keepAlive', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'anti-csrf-token': csrfToken
        }
      });
    }
  }

  // Calls the keep alive check based on user activity
  useEffect(() => {
    KEEPALIVE_EVENT_LISTENERS.forEach((eventType) => {
      document.body.addEventListener(eventType, keepAlive);
    });

    return () => {
      KEEPALIVE_EVENT_LISTENERS.forEach((eventType) => {
        document.body.removeEventListener(eventType, keepAlive);
      });
    }
  }, []);

  // Logs the user out when their idle check times out
  const onIdle = () => {
    window.location = '/logout/index';
  };

  // Opens the warning modal when the warning prompt event fires
  const onPrompt = () => {
    setOpen(true);
  };

  // Closes the warning modal when the user is active again
  const onActive = () => {
    setOpen(false);
  };

  // Set up the idle timer
  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout: TIMEOUT,
    promptBeforeIdle: PROMPT_TIMEOUT
  });

  // Closes the warning modal and re-activates the idle timer
  const handleClose = () => {
    activate();
    setOpen(false);
  };

  // Handles "Stay Connected" button clicks by closing the warning modal and reactivating the Idle timer
  const handleStayConnected = () => {
    activate();
    setOpen(false);
  };

  // Handles "Log Out" button clicks by logging the user out
  const handleLogout = () => {
    window.location = '/logout/index';
  };

  return (
    <>
      {children}
      <Dialog
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>
          Your session is about to expire
        </DialogTitle>
        <DialogContent>
          <DialogContentText mb={1}>
            If you remain inactive you will be automatically logged out.
          </DialogContentText>
          <CountDown getRemainingTime={getRemainingTime} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout}>Logout</Button>
          <Button onClick={handleStayConnected} variant='contained'>
            Stay Connected
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
};

IdleChecker.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired
};

export default IdleChecker;
